import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import {
  Box, List, Collapse, ListItemText, ListItemIcon, ListItemButton, Typography,
} from '@mui/material';
//
import Iconify from './Iconify';

// ----------------------------------------------------------------------

const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(

  ({ theme }) => ({
    ...theme.typography.body2,
    height: 48,
    position: 'relative',
    textTransform: 'capitalize',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    color: theme.palette.grey[700],
    width: '83%',
    margin: 'auto',
    transition: 'background-color 0.3s, width 0.3s',

    '&:before': {
      top: 0,
      right: 0,
      width: 3,
      bottom: 0,
      content: "''",
      display: 'none',
      position: 'absolute',
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
      backgroundColor: theme.palette.primary.main,
    },
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      borderRadius: '8px',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      width: '83%',
      margin: 'auto',
    },
  }),
);

const ListItemIconStyle = styled(ListItemIcon)(
  ({ theme }) => ({
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.grey[500],
  }),
);

// ----------------------------------------------------------------------

function NavItem({ item, active }) {
  const theme = useTheme();
  const isActiveRoot = active(item.path);
  const {
    title, path, icon, info, children,
  } = item;
  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const activeRootStyle = {
    color: theme.palette.grey[900],
    bgcolor: alpha(theme.palette.grey[500], theme.palette.action.selectedOpacity),
    borderRadius: '8px',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: '83%',
    margin: 'auto',
  };

  const activeSubStyle = {
    color: 'text.primary',
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          <ListItemText primary={title} />
          {info && info}
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((childItem) => {
              const { title: childTitle, path: childPath } = childItem;
              const isActiveSub = active(childPath);

              return (
                <ListItemStyle
                  key={childTitle}
                  component={RouterLink}
                  to={childPath}
                  sx={{
                    ...(isActiveSub && activeSubStyle),
                  }}
                >
                  <ListItemIconStyle>
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        display: 'flex',
                        borderRadius: '50%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'text.disabled',
                        transition: (transitionTheme) => transitionTheme.transitions.create('transform'),
                        ...(isActiveSub && {
                          transform: 'scale(2)',
                          bgcolor: 'primary.main',
                        }),
                      }}
                    />
                  </ListItemIconStyle>
                  <ListItemText disableTypography primary={childTitle} />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle),
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      <ListItemText
        disableTypography // Disable default typography styles
        primary={(
          <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
            {title}
          </Typography>
      )}
      />
      {info && info}
    </ListItemStyle>
  );
}

NavItem.propTypes = {
  item: PropTypes.shape({}),
  active: PropTypes.func,
};

NavItem.defaultProps = {
  item: undefined,
  active: undefined,
};

export default function NavSection({ navConfig, ...other }) {
  const { pathname } = useLocation();
  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  return (
    <Box {...other}>
      <List disablePadding>
        {navConfig.map((item) => (
          <NavItem key={item.title} item={item} active={match} />
        ))}
      </List>
    </Box>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.arrayOf(PropTypes.shape({})),
};

NavSection.defaultProps = {
  navConfig: [],
};
